import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.slug !== slug && a.categorySlug === article?.categorySlug)
    .slice(0, 3);
  const category = categories.find((c) => c.slug === article?.categorySlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        <article className="pt-16">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: article.category, path: `/category/${article.categorySlug}` },
              { name: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  {article.isHot && (
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium flex items-center gap-0.5 whitespace-nowrap">
                      <i className="ri-fire-line text-[10px]"></i>热门
                    </span>
                  )}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-chat-3-line"></i>
                    {article.comments} 评论
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Cover Image */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-6 relative z-10">
            <div className="rounded-lg overflow-hidden h-[300px] md:h-[450px]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="py-10 md:py-14">
            <div className="px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div
                  className="prose-custom text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-400 mr-1">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Share */}
                <div className="mt-6 flex items-center gap-3">
                  <span className="text-xs text-foreground-400">分享到：</span>
                  {[
                    { icon: 'ri-weibo-line', label: '微博' },
                    { icon: 'ri-wechat-line', label: '微信' },
                    { icon: 'ri-link', label: '复制链接' },
                  ].map((item) => (
                    <button
                      key={item.label}
                      className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                      aria-label={item.label}
                    >
                      <i className={`${item.icon} text-sm`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="px-4 md:px-6">
                <div className="max-w-7xl mx-auto">
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.slug}`}
                        className="group rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer overflow-hidden"
                      >
                        <div className="h-40 overflow-hidden">
                          <img
                            src={rel.coverImage}
                            alt={rel.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {rel.title}
                          </h4>
                          <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground-400">
                            <span>{rel.publishDate}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {rel.views >= 10000 ? `${(rel.views / 10000).toFixed(1)}万` : rel.views}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />

      {/* Article Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.publishDate,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '吃瓜群',
              url: 'https://www.chiguaqun.com',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.chiguaqun.com/article/${article.slug}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.chiguaqun.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                  item: `https://www.chiguaqun.com/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}